import { Inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { environment } from '../../../../environments/environment';
import { FrameLoadError } from '../../../cross-team-shared/misc-services/resources/url-request.service';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';
import { PointCloudManifest } from '../../../team-apis/otterdam/asset-manifests';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { SensorRendererState3D } from '../../state/sensor-renderer-state';
import { AssetLoadError, determineAssetLoadErrorMessage } from '../misc-utils';
import { LoadedFrameInfo } from './point-cloud-asset.service';

interface Asset {
  eventTrackingValue?: EventTrackingValue;
  sensorFrames: SensorRendererState3D['sensorFrames'];
}

interface LoadedFrameStats {
  loadStart: number;
  failedFrames: number;
  retriedFrames: number;
  retrievedFrames: number;
  totalFrames: number;
  totalRetryCount: number;
}

@Injectable()
export class PointCloudAssetEventService extends InjectableSuperclass {
  private eventTrackingStats!: LoadedFrameStats;

  constructor(
    private store: PointCloudContentStore,
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
  ) {
    super();
  }

  trackLoadedFrame(frame: LoadedFrameInfo | { error: FrameLoadError }) {
    if ('error' in frame) {
      ++this.eventTrackingStats.failedFrames;
      ++this.eventTrackingStats.retriedFrames;
      this.eventTrackingStats.totalRetryCount += frame.error.retryCount;
      this.trackAllFramesLoaded();
      return;
    }

    ++this.eventTrackingStats.retrievedFrames;
    if (frame.retryCount > 0) {
      this.eventTrackingStats.totalRetryCount += frame.retryCount;
      ++this.eventTrackingStats.retriedFrames;
    }

    if (
      this.eventTrackingStats.retrievedFrames ===
      this.eventTrackingStats.totalFrames
    ) {
      this.trackAllFramesLoaded();
    }
  }

  initPerFrameEventTracking(manifest: PointCloudManifest) {
    this.eventTrackingStats = {
      failedFrames: 0,
      loadStart: performance.now(),
      retriedFrames: 0,
      retrievedFrames: 0,
      totalFrames: manifest.cloud_asset_ids.length,
      totalRetryCount: 0,
    };
  }

  handleLoadFailure(error: any, assetLoadStart?: number) {
    let reason: string;
    if (error instanceof AssetLoadError) {
      reason = error.message;
    } else {
      reason = determineAssetLoadErrorMessage(error);
    }

    if (assetLoadStart) {
      this.trackAssetLoadFailureEvent(assetLoadStart, reason);
    }

    this.shellConnector.onAssetLoadError(reason);
  }

  handleLoadSuccess(asset: Asset) {
    const sensorRendererState = new SensorRendererState3D(asset.sensorFrames);
    this.store('sensorRendererState').set(sensorRendererState);
    this.store('status').set('ready');
    return {
      width: 0,
      height: 0,
      frameCount: asset.sensorFrames.length,
      // If the first frame is loaded, then it's a zip asset so all other
      // frames are loaded too
      allFramesLoaded: isDefined(asset.sensorFrames[0]),
    };
  }

  handleFrameLoadSuccess(loadedFrameNumber: number) {
    if (loadedFrameNumber === this.store.state().render.frameNumber) {
      // frame_number must be explicitly passed because the core team hasn't yet
      // added the frame_number to the context (if dealing with the first frame)
      this.shellConnector.trackEvent(
        {
          id: 'load_visible_frame',
          bucket: 'untracked',
          frame_number: loadedFrameNumber,
        },
        { isInteractive: false },
      );
    }
  }

  private trackAssetLoadFailureEvent(assetLoadStart: number, reason: string) {
    const url = this.store.getInputUrl();
    if (!url || !isAssetServiceUrl(url)) {
      this.shellConnector.trackEvent(
        {
          id: 'load_asset_failure',
          bucket: 'untracked',
          ...assetLoadEventValue(assetLoadStart, url),
          type: 'point_cloud',
          reason,
        },
        { isInteractive: false },
      );
    }
  }

  private trackAllFramesLoaded() {
    this.shellConnector.trackEvent(
      {
        id: 'load_all_per_frame_assets',
        bucket: 'untracked',
        duration: performance.now() - this.eventTrackingStats.loadStart,
        failed_frames: this.eventTrackingStats.failedFrames,
        retried_frames: this.eventTrackingStats.retriedFrames,
        retrieved_frames: this.eventTrackingStats.retrievedFrames,
        total_frames: this.eventTrackingStats.totalFrames,
        total_retry_count: this.eventTrackingStats.totalRetryCount,
      },
      { isInteractive: false },
    );
  }
}

function assetLoadEventValue(start: number, url: string | undefined) {
  const value: EventTrackingValue = { duration: performance.now() - start };
  if (url) {
    value.cacheable_url = url.startsWith('https://cacheable-client-assets');
  }
  return value;
}

function isAssetServiceUrl(url: string) {
  return url.startsWith(environment.assetServiceBaseUrl);
}
